import { CheckIcon, DisabledIcon } from "@xola/icons";
import { PurchaseItemStatus } from "common";
import { isArray } from "lodash";
import React from "react";
import { Audit, Purchase, PurchaseItem } from "types";
import { TimelineItem } from "../TimelineItem";

interface SpecificStatusTimelineItemProps {
    purchase: Purchase;
    item: PurchaseItem;
    audit: Audit;
    showDate?: boolean;
}

export const DeclinedTimelineItem = ({ purchase, item, audit, showDate = false }: SpecificStatusTimelineItemProps) => {
    return (
        <StatusTimelineItem
            purchase={purchase}
            item={item}
            audit={audit}
            status={PurchaseItemStatus.DECLINED}
            showDate={showDate}
        />
    );
};

DeclinedTimelineItem.supportsAudit = (_purchase: Purchase, item: PurchaseItem, audit: Audit) => {
    return StatusTimelineItem.supportsAudit(_purchase, item, audit, PurchaseItemStatus.DECLINED);
};

export const AcceptedTimelineItem = ({ purchase, item, audit, showDate = false }: SpecificStatusTimelineItemProps) => {
    return (
        <StatusTimelineItem
            purchase={purchase}
            item={item}
            audit={audit}
            status={PurchaseItemStatus.CONFIRMED}
            showDate={showDate}
        />
    );
};

AcceptedTimelineItem.supportsAudit = (_purchase: Purchase, item: PurchaseItem, audit: Audit) => {
    return StatusTimelineItem.supportsAudit(_purchase, item, audit, PurchaseItemStatus.CONFIRMED);
};

interface StatusTimelineItemProps {
    purchase: Purchase;
    item: PurchaseItem;
    audit: Audit;
    showDate?: boolean;
    status: string;
}

const StatusTimelineItem = ({ purchase, item, audit, status, showDate = false }: StatusTimelineItemProps) => {
    if (!StatusTimelineItem.supportsAudit(purchase, item, audit, status) || !("arrivalDateTime" in item)) {
        return null;
    }

    const { title = "N/A", icon, color = "primary" } = titleMap[status];
    const hasArrivalDateTime = "arrivalDateTime" in item;

    return hasArrivalDateTime ? (
        <TimelineItem title={title} audit={audit} icon={icon} color={color} showDate={showDate} purchase={purchase} />
    ) : null;
};

StatusTimelineItem.supportsAudit = (_purchase: Purchase, item: PurchaseItem, audit: Audit, status: string) => {
    return doesStatusMatch(item, audit, status);
};

const titleMap: { [key: string]: any } = {
    [PurchaseItemStatus.CONFIRMED]: { title: "Accepted", icon: <CheckIcon />, color: "primary" },
    [PurchaseItemStatus.DECLINED]: { title: "Declined", icon: <DisabledIcon />, color: "danger" },
};

const doesStatusMatch = (item: PurchaseItem, audit: Audit, status: string) => {
    return (
        isArray(audit.data?.items) &&
        audit.data?.items?.some((auditItem: any) => auditItem?.id === item?.id && auditItem.status?.new === status)
    );
};
